/* eslint-disable no-unused-vars */
import * as React from 'react';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import { Alert, Button, Checkbox, FormControlLabel, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack, TextField, FormControl, Select, ListItemText, Typography, CircularProgress, Tooltip, Box } from '@mui/material';

import { Dropdown, DropdownMenuItem, DropdownNestedMenuItem } from './dropdown';
import ArrowRight from "@mui/icons-material/ArrowRight";
import { AUTOMATICALLY_REPORT_TYPE, REPORT_FILE_TYPE } from '../../types/report.type';
import _ from "lodash";
import { CHART_TAB_BUTTON } from '../../types/chart-dialog.type';
import { useSelector } from "react-redux";
import { createSetting, getSettings } from '../../services/settings.service';
import { PAGE_TYPE } from '../../types/pages.type';
import AlertReportCustomDialog from './AlertReportCustomDialog';
import { useDashboard } from '../../context/DashboardContext';
import ReportSummary from './ReportSummary';
import { generateRichReportData } from '../../utilis/report-description';
import useTutorialTrigger from '../../hooks/useTutorialTrigger';
import { TUTORIAL_FEATURES } from '../../config/tutorials.config';


const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 5.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

export default function ReportButton({
    handleOnClickReport,
    handleExcelReport,
    loading = false,
    title,
    sx,
    selectedTab,
    selectedTabSetting,
    handleExcelReportCustom,
    tableFilterType,
    selectedQuestions,
    pageType,
    subPageType,
    handleSaveExcelColumn,
    cardType,
    openFrom = "main",
    isMainCard = false,
    totalCardType,
    isCustomAlertEnable = true,
    isADT = false,
    customCardData = null,
    disabled = false,
    isMobile = false,
    hideSummary = false,
    handleOnClickShareReport
}) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const { triggerTutorial, trackFeatureUsage } = useTutorialTrigger();
    const [reportOptions, setReportOptions] = React.useState();
    const anchorRef = React.useRef(null);
    const menuRef = React.useRef(null);
    
    // Handler for dropdown open/close - Dropdown component expects anchor element or null
    const handleDropdownOpen = React.useCallback((el) => {
        setAnchorEl(el);
    }, []);
    
    const open = !!anchorEl;
    
    // Handle click outside to close menu
    React.useEffect(() => {
        if (!open || !anchorEl) return;
        
        const handleClickOutside = (event) => {
            // Check if click is on the anchor button itself
            if (anchorRef.current && anchorRef.current.contains(event.target)) {
                return;
            }
            
            // Find the menu associated with this anchor element
            // Material-UI Menu renders in a portal, so we need to find it by checking all menus
            const allMenus = document.querySelectorAll('[role="menu"]');
            let clickedInsideMenu = false;
            
            for (const menu of allMenus) {
                // Check if this menu is visible (open) and contains the click target
                if (menu.contains(event.target)) {
                    // Verify this menu is associated with our anchor by checking if it's positioned near it
                    const menuRect = menu.getBoundingClientRect();
                    const anchorRect = anchorEl.getBoundingClientRect();
                    
                    // If menu is visible and near the anchor, it's likely our menu
                    if (menuRect.width > 0 && menuRect.height > 0) {
                        clickedInsideMenu = true;
                        break;
                    }
                }
            }
            
            if (clickedInsideMenu) {
                return;
            }
            
            // Click is outside both anchor and menu, close the menu
            setAnchorEl(null);
        };
        
        // Add event listener with a slight delay to avoid immediate closure
        const timeoutId = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside, true);
        }, 0);
        
        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('mousedown', handleClickOutside, true);
        };
    }, [open, anchorEl]);
    const [loading2, setLoading2] = React.useState(false);
    const [savingReport, setSavingReport] = React.useState(false);
    const [saveReportError, setSaveReportError] = React.useState(null);
    const [isCustomReport, setIsCustomReport] = React.useState(false);
    const { dashboardData } = useDashboard();

    // Handle AI pre-filled report and alert data
    React.useEffect(() => {        
        // Expose dialog trigger globally for AI to use
        window.reportDialogTrigger = (aiReportData) => {
            // Add flag to indicate this came from AI
            const reportDataWithAI = {
                ...aiReportData,
                fromAI: true
            };
            setReportOptions(reportDataWithAI);
        };

        // Expose alert dialog trigger globally for AI to use
        window.alertDialogTrigger = (aiAlertData) => {
            // Store alert data temporarily and open the alert dialog
            window.aiPrefilledAlertData = aiAlertData;
            setIsCustomReport(true);
        };

        // Check for AI pre-filled data on mount
        if (window.aiPrefilledReportData) {
            setReportOptions(window.aiPrefilledReportData);
            window.aiPrefilledReportData = null; // Clear after use
        }
        
        // Check for AI pre-filled alert data on mount
        if (window.aiPrefilledAlertData) {
            // Don't clear the data yet - let AlertReportCustomDialog handle it
            setIsCustomReport(true); // Open the alert dialog
        }

        return () => {
            // Cleanup
            window.reportDialogTrigger = null;
            window.alertDialogTrigger = null;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Intentionally empty - only register once on mount


    const handleAlertCustomReport = React.useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsCustomReport(true)
    }, []);

    const handleGenerateReport = React.useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        handleOnClickReport(title)
    }, [title, handleOnClickReport]);

    const handleShareReport = React.useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (handleOnClickShareReport) {
            handleOnClickShareReport(title);
        }
    }, [title, handleOnClickShareReport]);

    const [selectedColumns, setSelectedColumns] = React.useState([]);
    const [hasAttemptedSave, setHasAttemptedSave] = React.useState(false);
    const [duplicateNameError, setDuplicateNameError] = React.useState(false);

    const activeFacilities = useSelector((state) => state.activeFacilities.facilities);

    // Get relevant dashboard data based on page type
    const dashboardState = useSelector((state) => {
        switch (pageType) {
            case PAGE_TYPE.COMMUNITY_TRANSFER:
                return state.communityTransfer;
            case PAGE_TYPE.HOSPITAL:
                return state.hospital;
            case PAGE_TYPE.ADMISSION:
                return state.admission;
            case PAGE_TYPE.DECEASED:
                return state.deceased;
            case PAGE_TYPE.OVERALL:
                return state.overall;
            default:
                return {};
        }
    });

    const {
        cardFilter = {},
        transferType = null,
        filterListData = {},
        lockedTotalBy = null,
        percentageBy = null
    } = dashboardState;

    // Get enriched filter items from common store (saved by HeaderFilter)
    const headerFilterItems = useSelector((state) => state.common.headerFilterItems);

    const handleOnClickReportCustomExcel = React.useCallback((e) => {
        if (reportOptions?.reportFileType === "excel" && tableFilterType !== "all") {
            createSetting({
                selectedColumns,
                selectedTab: selectedTabSetting,
                pageType: subPageType === PAGE_TYPE.ADT ? subPageType : pageType,
                ...subPageType === PAGE_TYPE.ADT && { subPageType }
            });
        }

    }, [reportOptions?.reportFileType, selectedColumns, selectedTabSetting, pageType, subPageType, tableFilterType]);

    const handleSaveReportOption = React.useCallback(async (e) => {
        // Validate report name is required
        if (!reportOptions?.name || reportOptions.name.trim() === '') {
            setHasAttemptedSave(true);
            return;
        }

        // Reset validation state
        setHasAttemptedSave(false);
        setDuplicateNameError(false);
        setSaveReportError(null);
        setSavingReport(true);

        try {
            // Generate rich report data using shared utility
            const richReportData = generateRichReportData({
                reportOptions,
                pageType,
                title,
                headerFilterItems,
                selectedColumns,
                tableFilterType,
                cardType
            });

            // Save the rich JSON data as the description
            const descriptionData = JSON.stringify(richReportData);

            const result = await handleOnClickReport(title, {
                ...reportOptions,
                selectedColumns: selectedColumns,
                description: descriptionData
            });
            
            // Check if there was a duplicate name error
            if (result?.error === 'DUPLICATE_REPORT_NAME') {
                setDuplicateNameError(true);
                return;
            } else {
                handleOnClickReportCustomExcel();
                setReportOptions(null);
            }
        } catch (error) {
            // Check if it's a duplicate report name error
            if (error?.response?.data?.error === 'DUPLICATE_REPORT_NAME') {
                setDuplicateNameError(true);
            } else {
                setSaveReportError('Something went wrong. Please try again.');
            }
        } finally {
            setSavingReport(false);
        }
    }, [handleOnClickReport, title, reportOptions, selectedColumns, handleOnClickReportCustomExcel, pageType, headerFilterItems, tableFilterType, cardType]);

    const handleChange = (event) => {
        const { value } = event.target;

        if (Array.isArray(value) && value.length === 1 && value[0] === "selectAll") {
            return;
        }

        const parsedValue = typeof value === 'string' ? value.split(',') : value;
        setSelectedColumns(parsedValue);
    };

    const handleChangeSelectAll = async (event) => {
        const value = event.target.checked;
        if (value) {
            const values = selectedQuestions?.filter((ele) => ele?.question?.accessor !== "transferType")?.map((ele) => ele?.question?.accessor);
            setSelectedColumns([...values, "selectAll"]);
        } else {
            setSelectedColumns([]);
        }
    }


    const getSettingsData = async (filter) => {
        setLoading2(true)
        const res = await getSettings(filter);
        setLoading2(false);
        if (res && res.length > 0) {
            setSelectedColumns(res[0]?.selectedColumns);
        }
    }

    React.useEffect(() => {
        if (reportOptions?.reportFileType === "excel") {
            getSettingsData({
                pageType: subPageType === PAGE_TYPE.ADT ? subPageType : pageType,
                selectedTab: selectedTabSetting,
                ...subPageType === PAGE_TYPE.ADT && { subPageType }
            })
        }
    }, [pageType, selectedTabSetting, subPageType, reportOptions?.reportFileType]);

    const handleGenerateReportTable = React.useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (tableFilterType === "all") {
            handleExcelReport()
        } else {
            handleExcelReportCustom()
        }
    }, [handleExcelReport, handleExcelReportCustom, tableFilterType]);

    // Stable callback functions for report options
    const setDailyReport = React.useCallback((e) => {
        e?.preventDefault();
        e?.stopPropagation();
        setReportOptions(prevState => ({
            ...prevState,
            type: AUTOMATICALLY_REPORT_TYPE.DAILY
        }));
    }, []);

    const setWeeklyReport = React.useCallback((e) => {
        e?.preventDefault();
        e?.stopPropagation();
        setReportOptions(prevState => ({
            ...prevState,
            type: AUTOMATICALLY_REPORT_TYPE.WEEKLY
        }));
    }, []);

    const setMonthlyReport = React.useCallback((e) => {
        e?.preventDefault();
        e?.stopPropagation();
        setReportOptions(prevState => ({
            ...prevState,
            type: AUTOMATICALLY_REPORT_TYPE.MONTHLY
        }));
    }, []);

    const setQuarterlyReport = React.useCallback((e) => {
        e?.preventDefault();
        e?.stopPropagation();
        setReportOptions(prevState => ({
            ...prevState,
            type: AUTOMATICALLY_REPORT_TYPE.QUARTERLY
        }));
    }, []);

    const setYearlyReport = React.useCallback((e) => {
        e?.preventDefault();
        e?.stopPropagation();
        setReportOptions(prevState => ({
            ...prevState,
            type: AUTOMATICALLY_REPORT_TYPE.YEARLY
        }));
    }, []);

    const setPdfDailyReport = React.useCallback((e) => {
        e?.preventDefault();
        e?.stopPropagation();
        setReportOptions(prevState => ({
            ...prevState,
            reportFileType: REPORT_FILE_TYPE.PDF,
            type: AUTOMATICALLY_REPORT_TYPE.DAILY
        }));
    }, []);

    const setPdfWeeklyReport = React.useCallback((e) => {
        e?.preventDefault();
        e?.stopPropagation();
        setReportOptions(prevState => ({
            ...prevState,
            reportFileType: REPORT_FILE_TYPE.PDF,
            type: AUTOMATICALLY_REPORT_TYPE.WEEKLY
        }));
    }, []);

    const setPdfMonthlyReport = React.useCallback((e) => {
        e?.preventDefault();
        e?.stopPropagation();
        setReportOptions(prevState => ({
            ...prevState,
            reportFileType: REPORT_FILE_TYPE.PDF,
            type: AUTOMATICALLY_REPORT_TYPE.MONTHLY
        }));
    }, []);

    const setPdfQuarterlyReport = React.useCallback((e) => {
        e?.preventDefault();
        e?.stopPropagation();
        setReportOptions(prevState => ({
            ...prevState,
            reportFileType: REPORT_FILE_TYPE.PDF,
            type: AUTOMATICALLY_REPORT_TYPE.QUARTERLY
        }));
    }, []);

    const setPdfYearlyReport = React.useCallback((e) => {
        e?.preventDefault();
        e?.stopPropagation();
        setReportOptions(prevState => ({
            ...prevState,
            reportFileType: REPORT_FILE_TYPE.PDF,
            type: AUTOMATICALLY_REPORT_TYPE.YEARLY
        }));
    }, []);

    const setExcelDailyReport = React.useCallback((e) => {
        e?.preventDefault();
        e?.stopPropagation();
        setReportOptions(prevState => ({
            ...prevState,
            reportFileType: REPORT_FILE_TYPE.EXCEL,
            type: AUTOMATICALLY_REPORT_TYPE.DAILY
        }));
    }, []);

    const setExcelWeeklyReport = React.useCallback((e) => {
        e?.preventDefault();
        e?.stopPropagation();
        setReportOptions(prevState => ({
            ...prevState,
            reportFileType: REPORT_FILE_TYPE.EXCEL,
            type: AUTOMATICALLY_REPORT_TYPE.WEEKLY
        }));
    }, []);

    const setExcelMonthlyReport = React.useCallback((e) => {
        e?.preventDefault();
        e?.stopPropagation();
        setReportOptions(prevState => ({
            ...prevState,
            reportFileType: REPORT_FILE_TYPE.EXCEL,
            type: AUTOMATICALLY_REPORT_TYPE.MONTHLY
        }));
    }, []);

    const setExcelQuarterlyReport = React.useCallback((e) => {
        e?.preventDefault();
        e?.stopPropagation();
        setReportOptions(prevState => ({
            ...prevState,
            reportFileType: REPORT_FILE_TYPE.EXCEL,
            type: AUTOMATICALLY_REPORT_TYPE.QUARTERLY
        }));
    }, []);

    const setExcelYearlyReport = React.useCallback((e) => {
        e?.preventDefault();
        e?.stopPropagation();
        setReportOptions(prevState => ({
            ...prevState,
            reportFileType: REPORT_FILE_TYPE.EXCEL,
            type: AUTOMATICALLY_REPORT_TYPE.YEARLY
        }));
    }, []);

    const handleAutomatedAlertsTutorial = React.useCallback(() => {
        trackFeatureUsage(TUTORIAL_FEATURES.AUTOMATED_ALERTS).catch((error) => {
            console.error('Failed to track automated alerts usage:', error);
        });

        triggerTutorial(TUTORIAL_FEATURES.AUTOMATED_ALERTS).catch((error) => {
            console.error('Failed to trigger automated alerts tutorial:', error);
        });
    }, [trackFeatureUsage, triggerTutorial]);

    const SIMPLE_LIST_MENU = React.useMemo(() => [
        <DropdownMenuItem
            key="generate-report"
            onClick={handleGenerateReport}
            children={"Generate Report"}
        />,
        ...(handleOnClickShareReport ? [
            <DropdownMenuItem
                key="share-report"
                onClick={handleShareReport}
                children={"Share Report"}
            />
        ] : []),
        <DropdownNestedMenuItem
            key="automated-report"
            label="Set up Automated Report"
            rightIcon={<ArrowRight />}
            onClick={handleAutomatedAlertsTutorial}
            menu={[
                <DropdownMenuItem
                    key="daily"
                    onClick={setDailyReport}
                >
                    Daily
                </DropdownMenuItem>,
                <DropdownMenuItem
                    key="weekly"
                    onClick={setWeeklyReport}
                >
                    Weekly
                </DropdownMenuItem>,
                <DropdownMenuItem
                    key="monthly"
                    onClick={setMonthlyReport}
                >
                    Monthly
                </DropdownMenuItem>,
                <DropdownMenuItem
                    key="quarterly"
                    onClick={setQuarterlyReport}
                >
                    Quarterly
                </DropdownMenuItem>,
                <DropdownMenuItem
                    key="yearly"
                    onClick={setYearlyReport}
                >
                    Yearly
                </DropdownMenuItem>
            ]}
        />,
        ...(isCustomAlertEnable && !isADT && dashboardData?.page !== PAGE_TYPE.OVERALL ? [<DropdownMenuItem
            key="automated-alert"
            onClick={(event) => {
                handleAutomatedAlertsTutorial();
                handleAlertCustomReport(event);
            }}
            children={"Set up Automated Alert"}
        />
        ] : [])
    ], [handleGenerateReport, handleAlertCustomReport, handleAutomatedAlertsTutorial, isCustomAlertEnable, isADT, dashboardData?.page, setDailyReport, setWeeklyReport, setMonthlyReport, setQuarterlyReport, setYearlyReport, handleOnClickShareReport, handleShareReport]);

    const TABLE_LIST_MENU = React.useMemo(() => [
        <DropdownMenuItem
            key="generate-report-table"
            children="Generate Report"
            onClick={handleGenerateReportTable}
        />,
        ...(handleOnClickShareReport ? [
            <DropdownMenuItem
                key="share-report-table"
                children="Share Report"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (handleOnClickShareReport) {
                        handleOnClickShareReport(title);
                    }
                }}
            />
        ] : []),
        <DropdownNestedMenuItem
            key="automated-report-table"
            label="Set up Automated Report"
            rightIcon={<ArrowRight />}
            onClick={handleAutomatedAlertsTutorial}
            menu={[
                <DropdownNestedMenuItem
                    key="pdf-menu"
                    label="Pdf"
                    rightIcon={<ArrowRight />}
                    menu={[
                        <DropdownMenuItem
                            key="pdf-daily"
                            onClick={setPdfDailyReport}
                        >
                            Daily
                        </DropdownMenuItem>,
                        <DropdownMenuItem
                            key="pdf-weekly"
                            onClick={setPdfWeeklyReport}
                        >
                            Weekly
                        </DropdownMenuItem>,
                        <DropdownMenuItem
                            key="pdf-monthly"
                            onClick={setPdfMonthlyReport}
                        >
                            Monthly
                        </DropdownMenuItem>,
                        <DropdownMenuItem
                            key="pdf-quarterly"
                            onClick={setPdfQuarterlyReport}
                        >
                            Quarterly
                        </DropdownMenuItem>,
                        <DropdownMenuItem
                            key="pdf-yearly"
                            onClick={setPdfYearlyReport}
                        >
                            Yearly
                        </DropdownMenuItem>
                    ]} />,
                <DropdownNestedMenuItem
                    key="excel-menu"
                    label="Excel"
                    rightIcon={<ArrowRight />}
                    menu={[
                        <DropdownMenuItem
                            key="excel-daily"
                            onClick={setExcelDailyReport}
                        >
                            Daily
                        </DropdownMenuItem>,
                        <DropdownMenuItem
                            key="excel-weekly"
                            onClick={setExcelWeeklyReport}
                        >
                            Weekly
                        </DropdownMenuItem>,
                        <DropdownMenuItem
                            key="excel-monthly"
                            onClick={setExcelMonthlyReport}
                        >
                            Monthly
                        </DropdownMenuItem>,
                        <DropdownMenuItem
                            key="excel-quarterly"
                            onClick={setExcelQuarterlyReport}
                        >
                            Quarterly
                        </DropdownMenuItem>,
                        <DropdownMenuItem
                            key="excel-yearly"
                            onClick={setExcelYearlyReport}
                        >
                            Yearly
                        </DropdownMenuItem>
                    ]} />,
            ]}
        />
    ], [handleAutomatedAlertsTutorial, handleGenerateReportTable, setPdfDailyReport, setPdfWeeklyReport, setPdfMonthlyReport, setPdfQuarterlyReport, setPdfYearlyReport, setExcelDailyReport, setExcelWeeklyReport, setExcelMonthlyReport, setExcelQuarterlyReport, setExcelYearlyReport, handleOnClickShareReport, title]);


    return (
        <>
            {handleOnClickReport && !loading && (
                <>
                    <Stack direction="row" spacing={2}>
                        <div>
                            <div ref={menuRef}>
                                <Dropdown
                                    trigger={
                                        <div>
                                            <Tooltip title="Reports" arrow>
                                                <IconButton
                                                    sx={{
                                                        ...(!isMainCard && {
                                                            width: openFrom === "card" ? 22 : 29,
                                                            height: openFrom === "card" ? 22 : 29,
                                                            mt: 0,
                                                            mr: 0.5,
                                                            borderRadius: openFrom === "card" ? "50%" : 0.8,
                                                            border: "1px solid grey",
                                                            backgroundColor: "#fff",
                                                            color: loading ? "inherit" : "black",
                                                            ...sx,
                                                            ":hover": disabled ? {} : { backgroundColor: "grey", color: "#fff" },
                                                        }),
                                                        ...(isMainCard && {
                                                            mr: "-2px",
                                                        }),
                                                    }}
                                                    disableFocusRipple={true}
                                                    disableRipple={isMainCard ? true : false}
                                                    ref={anchorRef}
                                                    id="composition-button"
                                                    aria-controls={open ? 'composition-menu' : undefined}
                                                    aria-expanded={open ? 'true' : undefined}
                                                    aria-haspopup="true"
                                                    disabled={disabled}
                                                    onClick={() => {
                                                        trackFeatureUsage(TUTORIAL_FEATURES.GENERATING_REPORTS).catch((error) => {
                                                            console.error('Failed to track generating reports usage:', error);
                                                        });

                                                        triggerTutorial(TUTORIAL_FEATURES.GENERATING_REPORTS).catch((error) => {
                                                            console.error('Failed to trigger generating reports tutorial:', error);
                                                        });
                                                    }}
                                                >
                                                    <DriveFileMoveOutlinedIcon
                                                        sx={{
                                                            fontSize: openFrom === "card" ? 15 : 18,
                                                            ...(isMainCard && {
                                                                transition: "fill 0.2s ease-in-out",
                                                                "&:hover": { fill: "#9ea7b3" }
                                                            })
                                                        }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                    }
                                    menu={selectedTab === CHART_TAB_BUTTON.TABLE ? TABLE_LIST_MENU : SIMPLE_LIST_MENU}
                                    isOpen={anchorEl}
                                    onOpen={handleDropdownOpen}
                                    isMobile={isMobile}
                                    sx={{
                                        ...(isMobile && {
                                            '& .MuiMenu-root': {
                                                zIndex: 9999,
                                            },
                                            '& .MuiPaper-root': {
                                                zIndex: 9999,
                                            }
                                        }),
                                        // Override backdrop to allow clicks to close menu
                                        '& .MuiBackdrop-root': {
                                            pointerEvents: 'auto !important'
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </Stack>
                    {!!reportOptions && (
                        <Dialog open={true} fullWidth maxWidth="sm">
                            <DialogTitle>Send Automated {_.capitalize(reportOptions?.type)} Report</DialogTitle>
                            <DialogContent sx={{ mt: 1 }}>                                
                                <Stack>
                                    <TextField
                                        fullWidth
                                        label="Report Name"
                                        variant="standard"
                                        required
                                        value={reportOptions?.name || ''}
                                        error={(hasAttemptedSave && (!reportOptions?.name || reportOptions.name.trim() === '')) || duplicateNameError}
                                        helperText={
                                            hasAttemptedSave && (!reportOptions?.name || reportOptions.name.trim() === '')
                                                ? "Report name is required"
                                                : duplicateNameError
                                                    ? "Report name already exists for this account"
                                                    : ""
                                        }
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            setHasAttemptedSave(false); // Clear error when user starts typing
                                            setDuplicateNameError(false); // Clear duplicate name error when user starts typing
                                            setReportOptions(prevState => ({
                                                ...prevState,
                                                name: e.target.value
                                            }))
                                        }}
                                    />

                                    {/* Report Configuration Summary */}
                                    {!hideSummary && (
                                        <Box sx={{ mt: 2 }}>
                                            <ReportSummary
                                                reportOptions={reportOptions}
                                                pageType={pageType}
                                                title={reportOptions?.title || title}
                                                selectedColumns={selectedColumns}
                                                tableFilterType={tableFilterType}
                                                showTitle={true}
                                                compact={false}
                                                cardType={reportOptions?.cardType || cardType}  // ✅ Use AI cardType if available, otherwise prop
                                            />
                                        </Box>
                                    )}
                                    {reportOptions?.reportFileType === "excel" && tableFilterType !== "all" && (<>
                                        <FormControl fullWidth sx={{ mt: 2, mb: 1 }}>
                                            <Typography variant="subtitle2">Select the column you want to show in the excel report</Typography>
                                            {loading2 ? (
                                                <CircularProgress />
                                            ) : (
                                                <Select
                                                    labelId="column-selector-label"
                                                    id="column-selector"
                                                    multiple
                                                    fullWidth
                                                    value={selectedColumns?.filter(item => item !== "selectAll")}
                                                    onChange={handleChange}
                                                    MenuProps={MenuProps}
                                                    renderValue={(selected) => {
                                                        if (selected.length > 0) {
                                                            return selectedQuestions?.filter((ele) => _.includes(selected, ele?.question?.accessor) && ele?.question?.accessor !== "transferType").map((column) => column?.question?.label).join(', ')
                                                        }
                                                        return ""
                                                    }}
                                                >
                                                    <MenuItem key={"selectAll"} value={"selectAll"}>
                                                        <Checkbox onChange={handleChangeSelectAll} checked={selectedColumns.indexOf("selectAll") > -1} />
                                                        <ListItemText primary={"Select All"} />
                                                    </MenuItem>
                                                    {selectedQuestions && selectedQuestions?.length > 0 && selectedQuestions?.filter((ele) => ele?.question?.accessor !== "transferType").map((column) => (
                                                        <MenuItem key={column?.question?._id} value={column?.question?.accessor}>
                                                            <Checkbox checked={selectedColumns.indexOf(column?.question?.accessor) > -1} />
                                                            <ListItemText primary={column?.question?.label} />
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            )}
                                        </FormControl>
                                    </>
                                    )}
                                    {activeFacilities && activeFacilities.length > 1 && (
                                        <div className={`m-t-10 inputWrpr`}>
                                            <FormControlLabel
                                                label="Send report separate by facility"
                                                className="ffmr fs12"
                                                slotProps={{
                                                    typography: {
                                                        sx: {
                                                            mt: "10px",
                                                            fontSize: "13px",
                                                            fontFamily: "mont reg"
                                                        }
                                                    }
                                                }}
                                                control={
                                                    <Checkbox
                                                        checked={reportOptions?.isSendReportSeparate}
                                                        onChange={(e) => setReportOptions(prevState => ({
                                                            ...prevState,
                                                            isSendReportSeparate: e.target.checked
                                                        }))}
                                                    />
                                                }
                                            />
                                        </div>
                                    )}
                                </Stack>
                            </DialogContent>
                            {saveReportError && (
                                <Alert severity="error" sx={{ mx: 1.5, mt: 0, mb: 1 }} onClose={() => setSaveReportError(null)}>
                                    {saveReportError}
                                </Alert>
                            )}
                            <DialogActions sx={{ mb: 1, mr: 1.5 }}>
                                <Button
                                    disabled={savingReport}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setReportOptions(null);
                                        setHasAttemptedSave(false);
                                        setDuplicateNameError(false);
                                        setSaveReportError(null);
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    disabled={savingReport}
                                    onClick={handleSaveReportOption}
                                    startIcon={savingReport ? <CircularProgress size={16} color="inherit" /> : null}
                                >
                                    {savingReport ? 'Saving...' : 'Save'}
                                </Button>
                            </DialogActions>
                        </Dialog>
                    )}
                </>
            )}
            {isCustomReport &&
                <AlertReportCustomDialog
                    handleClose={() => setIsCustomReport(false)}
                    pageType={pageType}
                    cardType={cardType}
                    totalCardType={totalCardType}
                    customCardData={customCardData}
                />
            }
        </>
    )
}
